import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './ReminderFiltersScreen.module.scss';
import Button from '../../../common/components/button/Button';
import FormItem from '../../../common/components/formItem/FormItem';
import Label from '../../../common/components/label/Label';
import { Col, Row } from 'react-flexbox-grid';
import { SelectValueLabel } from '../../../common/types/SelectValueLabel';
import VehiclesService from '../../../api/vehicles/VehiclesService';
import ReminderCategoriesService from 'api/reminderCategories/ReminderCategoriesService';
import Select from 'common/components/select/Select';
import { Controller, useForm } from 'react-hook-form';
import Loading from 'common/services/Loading';
import { FaInfoCircle } from 'react-icons/fa';
import ReactTooltip from 'react-tooltip';
import Utils from 'common/services/Utils';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import RangeInputPicker from 'common/components/rangeInputPicker/RangeInputPicker';
import InputGroup from 'common/components/inputGroup/InputGroup';
import moment from 'moment';
import LocalsService from 'api/locals/LocalsService';
import VehicleSegmentsService from 'api/vehicleSegments/VehicleSegmentsService';

export interface Filters {
    vehicleId?: string;
    vehicleName?: string;
    categoryId?: string;
    categoryName?: string;
    statuses?: string[];
    alertDateFrom?: number;
    alertDateTo?: number;
    reminderDateFrom?: string;
    reminderDateTo?: string;
    alertKmFrom?: number;
    alertKmTo?: number;
    alertDaysPreset?: string;
    alertKmPreset?: string;
    criteriaType?: string;
    localIds?: string[];
    localNames?: string[];
    localOperator?: string;
    segmentId?: string;
    segmentName?: string;
    page?: number;
    orderColumn?: string;
    orderBy?: string;
}

type Props = {
    isVehicle?: boolean;
    isUser?: boolean;
    filters: Filters;
    onChange: (filters: Filters) => void;
    onFilter: (filters: Filters) => void;
}

const STATUS_OPTIONS: string[] = ['ACTIVE', 'EXPIRED', 'FINISHED'];

export const DAYS_PRESETS: { key: string; from: number; to: number }[] = [
    { key: 'today', from: 0, to: 0 },
    { key: 'tomorrow', from: 0, to: 1 },
    { key: 'in_3_days', from: 0, to: 3 },
    { key: 'in_1_week', from: 0, to: 7 },
    { key: 'in_2_weeks', from: 0, to: 14 },
    { key: 'in_1_month', from: 0, to: 30 },
    { key: 'in_3_months', from: 0, to: 90 },
    { key: 'in_6_months', from: 0, to: 180 },
];

export const KM_PRESETS: { key: string; from: number; to: number }[] = [
    { key: 'in_100_km', from: 0, to: 100 },
    { key: 'in_500_km', from: 0, to: 500 },
    { key: 'in_1000_km', from: 0, to: 1000 },
    { key: 'in_2000_km', from: 0, to: 2000 },
    { key: 'in_5000_km', from: 0, to: 5000 },
    { key: 'in_10000_km', from: 0, to: 10000 },
];

const ReminderFiltersScreen: React.FC<Props> = ({ isVehicle, filters, onChange, onFilter }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const form = useForm<Filters>({ shouldUnregister: false, shouldFocusError: true, defaultValues: filters });
    const { setValue, getValues, control, watch } = form;
    const [vehiclesOptions, setVehicles] = useState<SelectValueLabel[]>([]);
    const [categoriesOptions, setCategoryOptions] = useState<SelectValueLabel[]>([]);
    const [segmentsOptions, setSegments] = useState<SelectValueLabel[]>([]);
    const [localsOptions, setLocals] = useState<SelectValueLabel[]>([]);
    const [reRender, setReRender] = useState<number>(0);
    const [daysPreset, setDaysPreset] = useState<string | null>(filters.alertDaysPreset ?? null);
    const [kmPreset, setKmPreset] = useState<string | null>(filters.alertKmPreset ?? null);
    const [customDaysFrom, setCustomDaysFrom] = useState<number | undefined>(filters.alertDateFrom);
    const [customDaysTo, setCustomDaysTo] = useState<number | undefined>(filters.alertDateTo);
    const [customKmFrom, setCustomKmFrom] = useState<number | undefined>(filters.alertKmFrom);
    const [customKmTo, setCustomKmTo] = useState<number | undefined>(filters.alertKmTo);

    const criteriaOptions: SelectValueLabel[] = [
        { value: 'DATE', label: t('reminders.notifications.criteria.DATE') as string },
        { value: 'KMS', label: t('reminders.notifications.criteria.KMS') as string },
    ];

    const statusOptions: SelectValueLabel[] = STATUS_OPTIONS.map(s => ({
        value: s,
        label: t(`reminders.notifications.status.${s}` as any) as string,
    }));

    const getData = async () => {
        try {
            Loading.show();
            getVehiclesCatalog();
            const [_categoriesOptions, segmentsOptionsDB, localsOptionsDB] = await Promise.all([
                ReminderCategoriesService.catalogByReminders(),
                VehicleSegmentsService.catalog(),
                LocalsService.catalog()
            ]);
            setCategoryOptions(Utils.sortAlphabetically(_categoriesOptions.map(x => ({ value: x.id ?? '', name: x.designation, label: x.isSystem ? t(`reminder_categories.category_types.${x.designation}` as any) : x.designation }))));
            setSegments(Utils.sortAlphabetically(segmentsOptionsDB));
            setLocals([{ value: '00000000-0000-0000-0000-000000000000', label: t('maintenances.list.none') as string }, ...Utils.sortAlphabetically(localsOptionsDB)]);

            Loading.hide();
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REMINDERS, `Couldn't load reminders list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    const getVehiclesCatalog = async () => {
        try {
            Loading.show();
            const _vehiclesOptions = await VehiclesService.catalogByReminders({ withDeleted: true });
            setVehicles(_vehiclesOptions);
            Loading.hide();
        } catch (error) {
            Loading.hide();
            Logger.error(LOGGER_LOG_TYPE.REMINDERS, `Couldn't load vehicles list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        ReactTooltip.rebuild()
    }, []);

    const clearFilters = () => {
        form.reset({
            vehicleId: undefined,
            vehicleName: undefined,
            categoryId: undefined,
            categoryName: undefined,
            statuses: undefined,
            alertDateFrom: undefined,
            alertDateTo: undefined,
            alertKmFrom: undefined,
            alertKmTo: undefined,
            alertDaysPreset: undefined,
            alertKmPreset: undefined,
            criteriaType: undefined,
            localIds: [],
            localNames: [],
            localOperator: 'OR',
            segmentId: undefined,
            segmentName: undefined,
            reminderDateFrom: undefined,
            reminderDateTo: undefined,
            orderColumn: 'r.created_date',
            orderBy: 'desc',
        });
        setDaysPreset(null);
        setKmPreset(null);
        setCustomDaysFrom(undefined);
        setCustomDaysTo(undefined);
        setCustomKmFrom(undefined);
        setCustomKmTo(undefined);
        onInputChange();
        onFilter(getValues());
    }

    const onSubmit = () => {
        const merged = { ...getValues() };
        merged.alertDaysPreset = daysPreset ?? undefined;
        merged.alertKmPreset = kmPreset ?? undefined;
        if (daysPreset === 'other') {
            merged.alertDateFrom = customDaysFrom;
            merged.alertDateTo = customDaysTo;
        }
        if (kmPreset === 'other') {
            merged.alertKmFrom = customKmFrom;
            merged.alertKmTo = customKmTo;
        }
        onFilter(merged);
    };

    const daysPresetOptions: SelectValueLabel[] = [
        ...DAYS_PRESETS.map(p => ({ value: p.key, label: t(`reminders.filters.alert_days_presets.${p.key}` as any) as string })),
        { value: 'other', label: t('reminders.filters.alert_days_presets.other') as string },
    ];

    const kmPresetOptions: SelectValueLabel[] = [
        ...KM_PRESETS.map(p => ({ value: p.key, label: t(`reminders.filters.alert_km_presets.${p.key}` as any) as string })),
        { value: 'other', label: t('reminders.filters.alert_km_presets.other') as string },
    ];

    const onDaysPresetChange = (selected: SelectValueLabel | null) => {
        const key = selected?.value ?? null;
        setDaysPreset(key);
        if (!key) {
            setValue('alertDateFrom', undefined);
            setValue('alertDateTo', undefined);
        } else if (key === 'other') {
            setValue('alertDateFrom', undefined);
            setValue('alertDateTo', undefined);
        } else {
            const preset = DAYS_PRESETS.find(p => p.key === key);
            if (preset) {
                setValue('alertDateFrom', preset.from);
                setValue('alertDateTo', preset.to);
            }
        }
        onInputChange();
    };

    const onKmPresetChange = (selected: SelectValueLabel | null) => {
        const key = selected?.value ?? null;
        setKmPreset(key);
        if (!key) {
            setValue('alertKmFrom', undefined);
            setValue('alertKmTo', undefined);
        } else if (key === 'other') {
            setValue('alertKmFrom', undefined);
            setValue('alertKmTo', undefined);
        } else {
            const preset = KM_PRESETS.find(p => p.key === key);
            if (preset) {
                setValue('alertKmFrom', preset.from);
                setValue('alertKmTo', preset.to);
            }
        }
        onInputChange();
    };

    const onInputChange = () => {

        onChange(getValues());

        setReRender(reRender + 1);
    }

    return (
        <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className={styles.formContent}>
                <div>
                    <FormItem>
                        <Row key={'remindersFiltersRow' + reRender}>

                            {!isVehicle &&
                                <Col xs={12}>
                                    <FormItem>
                                        <Label className={styles.bold}>{t('vehicle.vehicle')}
                                            <FaInfoCircle data-tip={t('reminder_categories.only_vehicles_with_reminders')} data-for={'activesTooltip'} style={{ marginLeft: '0.5rem' }} />
                                        </Label>
                                        <ReactTooltip id={'activesTooltip'} place="right" type="dark" effect="solid" />

                                        <Controller
                                            render={({ onChange, value }) => {
                                                return (
                                                    <Select
                                                        options={vehiclesOptions}
                                                        isClearable
                                                        placeholder={t('vehicle.vehicle')}
                                                        onChange={(data: SelectValueLabel) => {
                                                            setValue('vehicleName', vehiclesOptions.find(x => x.value == data?.value)?.label ?? null);
                                                            onChange(data?.value ?? null);
                                                            onInputChange();
                                                        }}
                                                        value={vehiclesOptions.find(x => x.value === value) ? { label: vehiclesOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                    />
                                                );
                                            }}
                                            control={control}
                                            name="vehicleId"
                                            defaultValue={getValues('vehicleId')} />
                                    </FormItem>
                                </Col>}

                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('reminders.list.category')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={categoriesOptions}
                                                    isClearable
                                                    placeholder={t('reminders.list.category')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        const _category = categoriesOptions.find(x => x.value == data?.value);
                                                        setValue('categoryId', _category?.value ?? null);
                                                        setValue('categoryName', _category?.label ?? null);
                                                        onChange(data?.value ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={categoriesOptions.find(x => x.value === value) ? { label: categoriesOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="categoryId"
                                        defaultValue={getValues('categoryId')} />
                                </FormItem>
                            </Col>

                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('reminders.filters.status')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            const selectedValues = (value as string[] || []).map(v => statusOptions.find(o => o.value === v)).filter(Boolean) as SelectValueLabel[];
                                            return (
                                                <Select
                                                    options={statusOptions}
                                                    isMulti
                                                    isClearable
                                                    placeholder={t('reminders.filters.status')}
                                                    onChange={(data: SelectValueLabel[]) => {
                                                        const values = data ? data.map(d => d.value) : [];
                                                        onChange(values.length > 0 ? values : undefined);
                                                        onInputChange();
                                                    }}
                                                    value={selectedValues}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="statuses"
                                        defaultValue={getValues('statuses')} />
                                </FormItem>
                            </Col>

                            <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('reminders.list.criteria_type')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={criteriaOptions}
                                                    isClearable
                                                    placeholder={t('reminders.list.criteria_type')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onChange(data?.value ?? undefined);
                                                        onInputChange();
                                                    }}
                                                    value={criteriaOptions.find(x => x.value === value) ?? null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="criteriaType"
                                        defaultValue={getValues('criteriaType')} />
                                </FormItem>
                            </Col>

                            {!isVehicle && <Col xs={6}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('vehicle.type')}</Label>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            return (
                                                <Select
                                                    options={segmentsOptions}
                                                    isClearable
                                                    placeholder={t('vehicle.type')}
                                                    onChange={(data: SelectValueLabel) => {
                                                        onChange(data?.value ?? null);
                                                        setValue('segmentName', data?.label ?? null);
                                                        onInputChange();
                                                    }}
                                                    value={segmentsOptions.find(x => x.value === value) ? { label: segmentsOptions.find(x => x.value === value)?.label ?? '', value: value } : null}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="segmentId"
                                        defaultValue={getValues('segmentId')} />
                                </FormItem>
                            </Col>}

                            {!isVehicle && <Col xs={6}>
                                <FormItem>
                                    <div className={styles.labelWithOperator}>
                                        <Label className={styles.bold}>{t('vehicle.local')}</Label>
                                        <div className={styles.operatorToggle}>
                                            <span title={t('common.filter_all_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_all' as any) as string}
                                                    className={styles.firstChild}
                                                    preset={watch('localOperator') === 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'AND'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                            <span title={t('common.filter_any_tooltip' as any) as string}>
                                                <Button
                                                    text={t('common.filter_any' as any) as string}
                                                    className={styles.lastChild}
                                                    preset={watch('localOperator') !== 'AND' ? 'primary' : 'secondary'}
                                                    onClick={() => { setValue('localOperator', 'OR'); onInputChange(); }}
                                                    size={'small'}
                                                />
                                            </span>
                                        </div>
                                    </div>
                                    <Controller
                                        render={({ onChange, value }) => {
                                            const selectedValues = ((value as string[]) || []).map(v => localsOptions.find(o => o.value === v)).filter(Boolean) as SelectValueLabel[];
                                            return (
                                                <Select
                                                    options={localsOptions}
                                                    isMulti
                                                    isClearable
                                                    placeholder={t('vehicle.local')}
                                                    onChange={(data: SelectValueLabel[]) => {
                                                        const values = data ? data.map(d => d.value) : [];
                                                        const names = data ? data.map(d => d.label) : [];
                                                        onChange(values.length > 0 ? values : []);
                                                        setValue('localNames', names.length > 0 ? names : []);
                                                        onInputChange();
                                                    }}
                                                    value={selectedValues}
                                                />
                                            );
                                        }}
                                        control={control}
                                        name="localIds"
                                        defaultValue={getValues('localIds')} />
                                </FormItem>
                            </Col>}

                            <Col xs={12}>
                                <FormItem>
                                    <RangeInputPicker
                                        label={t('reminders.filters.reminder_date_range') as string}
                                        isDatePicker
                                        onChange={(startValue, endValue) => {
                                            setValue('reminderDateFrom', startValue ? moment(startValue).format('YYYY-MM-DD') : undefined);
                                            setValue('reminderDateTo', endValue ? moment(endValue).format('YYYY-MM-DD') : undefined);
                                        }}
                                        defaultStartValue={filters.reminderDateFrom ? new Date(filters.reminderDateFrom) : undefined}
                                        defaultEndValue={filters.reminderDateTo ? new Date(filters.reminderDateTo) : undefined}
                                    />
                                </FormItem>
                            </Col>

                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('reminders.filters.alert_date_range')}</Label>
                                    <div className={styles.alertFilterRow}>
                                        <div className={styles.alertFilterDropdown}>
                                            <Select
                                                options={daysPresetOptions}
                                                isClearable
                                                placeholder={t('reminders.filters.alert_date_range')}
                                                onChange={(data: SelectValueLabel) => onDaysPresetChange(data ?? null)}
                                                value={daysPreset ? daysPresetOptions.find(o => o.value === daysPreset) ?? null : null}
                                                menuPortalTarget={document.body}
                                                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                                            />
                                        </div>
                                        {daysPreset === 'other' && (
                                            <div className={styles.alertFilterRange}>
                                                <div className={styles.alertFilterInputLeft}>
                                                    <InputGroup
                                                        format="integer"
                                                        placeholder={t('common.range.from') as string}
                                                        value={customDaysFrom ?? ''}
                                                        onValueChange={v => {
                                                            setCustomDaysFrom(v != null && v !== '' ? Number(v) : undefined);
                                                        }}
                                                        text={t('common.range_separator') as string}
                                                        removeIconOrTextBorderRadius
                                                    />
                                                </div>
                                                <div className={styles.alertFilterInputRight}>
                                                    <InputGroup
                                                        format="integer"
                                                        placeholder={t('common.range.to') as string}
                                                        value={customDaysTo ?? ''}
                                                        onValueChange={v => {
                                                            setCustomDaysTo(v != null && v !== '' ? Number(v) : undefined);
                                                        }}
                                                        text={t('common.days') as string}
                                                        removeInputBorderRadius
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </FormItem>
                            </Col>

                            <Col xs={12}>
                                <FormItem>
                                    <Label className={styles.bold}>{t('reminders.filters.alert_km_range')}</Label>
                                    <div className={styles.alertFilterRow}>
                                        <div className={styles.alertFilterDropdown}>
                                            <Select
                                                options={kmPresetOptions}
                                                isClearable
                                                placeholder={t('reminders.filters.alert_km_range')}
                                                onChange={(data: SelectValueLabel) => onKmPresetChange(data ?? null)}
                                                value={kmPreset ? kmPresetOptions.find(o => o.value === kmPreset) ?? null : null}
                                                menuPortalTarget={document.body}
                                                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                                            />
                                        </div>
                                        {kmPreset === 'other' && (
                                            <div className={styles.alertFilterRange}>
                                                <div className={styles.alertFilterInputLeft}>
                                                    <InputGroup
                                                        format="integer"
                                                        placeholder={t('common.range.from') as string}
                                                        value={customKmFrom ?? ''}
                                                        onValueChange={v => {
                                                            setCustomKmFrom(v != null && v !== '' ? Number(v) : undefined);
                                                        }}
                                                        text={t('common.range_separator') as string}
                                                        removeIconOrTextBorderRadius
                                                    />
                                                </div>
                                                <div className={styles.alertFilterInputRight}>
                                                    <InputGroup
                                                        format="integer"
                                                        placeholder={t('common.range.to') as string}
                                                        value={customKmTo ?? ''}
                                                        onValueChange={v => {
                                                            setCustomKmTo(v != null && v !== '' ? Number(v) : undefined);
                                                        }}
                                                        text="km"
                                                        removeInputBorderRadius
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </FormItem>
                            </Col>


                        </Row>
                    </FormItem>
                </div>
                <div className={styles.buttonsFooter}>
                    <FormItem>
                        <Button
                            text={t('common.clear')}
                            size={'normal'}
                            preset={'secondary'}
                            onClick={clearFilters}
                            type='reset' />
                        <Button
                            type='submit'
                            text={t('common.apply')}
                            size={'normal'}
                        />
                    </FormItem>
                </div>
            </div>
        </form>
    );
}

export default memo(ReminderFiltersScreen);
